/**
 * DriveBreadcrumb — the space-driven breadcrumb bar shared by the desktop
 * DriveView and the mobile drive screen (one implementation, no parallel copy
 * to drift).
 *
 * Importance order: current folder > parents > root. When the row runs out of
 * width the ROOT gives up first (its name collapses to the HardDrive glyph,
 * full name on hover), then the hallway between root and current consolidates
 * into a … menu one crumb at a time (shallowest first), and only if all of
 * that still overflows does the current crumb itself truncate. Space-driven,
 * not depth-driven; the fit runs in layout effects (pre-paint) so intermediate
 * overflow states never flash, and re-climbs on any path change or resize.
 *
 * Drag-and-drop onto crumbs is DESKTOP-only and passed in as optional props —
 * mobile renders the identical bar without them.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChevronRight, HardDrive, LayoutGrid, MoreHorizontal } from 'lucide-react'
import { Tooltip, MiniappIcon, FileIcon } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { isMiniappFolder, isProjectFolder, stripProjectFolderExt, stripMiniappFolderExt } from '@papan/types'
import { useMiniappIconSpec } from './useMiniappIconSpec.js'

export interface Crumb { id: string; name: string }

/** A pointer row's name (`Work.chain`, `Projects.shared`). */
const POINTER_ENDING = /\.(?:chain|shared)$/i

/**
 * A breadcrumb name with the package signals (APP chip / project glyph) its
 * rows and cards use. The name renders inside a Tooltip whose span truncates —
 * the caller's flex container supplies the max-width — so parents clamp like
 * the workspace breadcrumb (full name on hover, only when actually truncated)
 * while badges stay whole.
 */
function CrumbLabel({ id, name }: { id: string; name: string }) {
  // Crumbs carry only {id, name} (the path-cache tree is lean); the hook
  // fetches the mini-app's custom icon spec on demand and keeps it live via
  // file:updated — no-ops for non-mini-app names. Falls back to the
  // name-derived default until (or unless) a custom spec resolves.
  const { spec: iconSpec, rev: iconRev } = useMiniappIconSpec(id, name)
  // I112 — a `.chain` / `.shared` crumb keeps its full name AND the pointer icon the
  // drive rows use (folder + link / network badge): a breadcrumb never hides that you
  // are inside a shortcut. (Crumbs carry no fileType, so the badge is name-derived —
  // FileIcon's own fallback for a name-only caller.)
  if (POINTER_ENDING.test(name)) {
    return (
      <>
        <FileIcon name={name} isFolder size={14} className="shrink-0" />
        <Tooltip content={name} className="truncate">{name}</Tooltip>
      </>
    )
  }
  if (isMiniappFolder(name)) {
    const display = stripMiniappFolderExt(name)
    return (
      <>
        <MiniappIcon spec={iconSpec} name={name} size={14} refId={id} rev={iconRev} className="shrink-0" />
        <Tooltip content={display} className="truncate">{display}</Tooltip>
        <span className="text-[9px] font-semibold tracking-wide px-1 py-px rounded file-badge-app shrink-0">APP</span>
      </>
    )
  }
  if (isProjectFolder(name)) {
    const display = stripProjectFolderExt(name)
    return (
      <>
        <LayoutGrid size={12} className="text-purple-400 shrink-0" />
        <Tooltip content={display} className="truncate">{display}</Tooltip>
      </>
    )
  }
  return <Tooltip content={name} className="truncate">{name}</Tooltip>
}

/**
 * Consolidated middle of a deep breadcrumb path: root › … › parent › current.
 * The "…" lists the hidden ancestors (shallowest first) and doubles as a drag
 * target — hovering it mid-drag opens the list so hidden crumbs stay droppable
 * (desktop only; the drag props are optional).
 */
function BreadcrumbOverflowMenu({ crumbs, dropTargetCrumb = null, onNavigate, onCrumbDragOver, onCrumbDragLeave, onCrumbDrop, t }: {
  crumbs: Crumb[]
  dropTargetCrumb?: string | null
  onNavigate: (id: string) => void
  onCrumbDragOver?: (e: React.DragEvent, id: string) => void
  onCrumbDragLeave?: (e: React.DragEvent) => void
  onCrumbDrop?: (e: React.DragEvent, id: string) => void
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // A drag-opened menu can't be dismissed by click-away (clicks don't fire
  // mid-drag), so it runs on a 1s inactivity fuse: every dragover inside the
  // ellipsis/menu re-arms it, and a drag that wanders off lets it burn down and
  // close. Click-opened menus never arm it — they keep outside-click dismissal.
  const armAutoClose = () => {
    clearTimeout(autoCloseRef.current)
    autoCloseRef.current = setTimeout(() => setOpen(false), 1000)
  }
  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onDragDone = () => setOpen(false)
    document.addEventListener('click', onDocClick, true)
    document.addEventListener('drop', onDragDone, true)
    document.addEventListener('dragend', onDragDone, true)
    return () => {
      clearTimeout(autoCloseRef.current)
      document.removeEventListener('click', onDocClick, true)
      document.removeEventListener('drop', onDragDone, true)
      document.removeEventListener('dragend', onDragDone, true)
    }
  }, [open])
  return (
    // dragover bubbles, so this one handler covers the ellipsis AND every menu
    // item: any drag activity inside opens the menu and re-arms the fuse.
    <div ref={ref} className="relative shrink-0" onDragOver={onCrumbDragOver ? () => { setOpen(true); armAutoClose() } : undefined}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="themed-text-secondary hover:themed-text px-2 py-1 rounded transition-colors"
        title={t('driveView.breadcrumb.showHiddenFolders')}
        data-testid="drive-breadcrumb-ellipsis"
      >
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <div className="absolute left-0 top-8 ws-menu z-20 min-w-[160px] max-w-[20rem] normal-case tracking-normal">
          {crumbs.map((crumb) => (
            <button
              key={crumb.id}
              type="button"
              onClick={() => { setOpen(false); onNavigate(crumb.id) }}
              onDragOver={onCrumbDragOver ? (e) => onCrumbDragOver(e, crumb.id) : undefined}
              onDragLeave={onCrumbDragLeave}
              onDrop={onCrumbDrop ? (e) => onCrumbDrop(e, crumb.id) : undefined}
              className={`ws-menu-item flex items-center gap-1.5 min-w-0 ${
                dropTargetCrumb === crumb.id ? 'bg-green-600/30 ring-2 ring-green-500' : ''
              }`}
            >
              <CrumbLabel id={crumb.id} name={crumb.name} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function DriveBreadcrumb({
  breadcrumbs,
  rootLabel,
  onNavigate,
  dropTargetCrumb = null,
  rootDropId,
  onCrumbDragOver,
  onCrumbDragLeave,
  onCrumbDrop,
  compact = false,
}: {
  breadcrumbs: Crumb[]
  /** A smaller bar for inline use (a dialog row) — same fit / … collapse behaviour. */
  compact?: boolean
  /** Drive-root label (personalized "My Drive" / "‹tenant›'s Drive"). */
  rootLabel: string
  /** null = navigate to the drive root; a crumb id otherwise. */
  onNavigate: (id: string | null) => void
  // ─── Desktop drag-onto-crumb (all optional; omit on mobile) ───
  dropTargetCrumb?: string | null
  /** Sentinel id used for the root button's drop target (e.g. DROP_ROOT). */
  rootDropId?: string
  onCrumbDragOver?: (e: React.DragEvent, id: string) => void
  onCrumbDragLeave?: (e: React.DragEvent) => void
  onCrumbDrop?: (e: React.DragEvent, id: string) => void
}) {
  const { t } = useTranslation('files')

  const crumbBarRef = useRef<HTMLDivElement>(null)
  const [squeezeRootCrumb, setSqueezeRootCrumb] = useState(false)
  const [hiddenCrumbCount, setHiddenCrumbCount] = useState(0)
  const [squeezeCurrentCrumb, setSqueezeCurrentCrumb] = useState(false)
  const [crumbFitTick, setCrumbFitTick] = useState(0)
  const maxHiddenCrumbs = Math.max(0, breadcrumbs.length - 1)
  useLayoutEffect(() => {
    setSqueezeRootCrumb(false)
    setHiddenCrumbCount(0)
    setSqueezeCurrentCrumb(false)
  }, [breadcrumbs])
  useLayoutEffect(() => {
    const el = crumbBarRef.current
    if (!el) return
    // Re-fit from scratch on any bar width change (window resize, side panel,
    // the search toolbar swapping in). The bar is flex-1, so its width doesn't
    // depend on its own content — consolidating crumbs can't re-trigger this.
    const ro = new ResizeObserver(() => {
      setSqueezeRootCrumb(false)
      setHiddenCrumbCount(0)
      setSqueezeCurrentCrumb(false)
      setCrumbFitTick((v) => v + 1)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  useLayoutEffect(() => {
    const el = crumbBarRef.current
    if (!el) return
    if (el.scrollWidth <= el.clientWidth) return
    if (!squeezeRootCrumb) setSqueezeRootCrumb(true)
    else if (hiddenCrumbCount < maxHiddenCrumbs) setHiddenCrumbCount(Math.min(hiddenCrumbCount + 1, maxHiddenCrumbs))
    else if (!squeezeCurrentCrumb) setSqueezeCurrentCrumb(true)
  }, [crumbFitTick, squeezeRootCrumb, hiddenCrumbCount, squeezeCurrentCrumb, maxHiddenCrumbs, breadcrumbs])

  // Everything in the bar is shrink-0 so an over-long path actually OVERFLOWS
  // (scrollWidth > clientWidth) instead of flex silently squeezing crumbs —
  // that overflow is what the fit effect measures. NO overflow-hidden: it would
  // clip the … dropdown and the drop-target rings, and the pre-paint fit loop
  // means an overflowing state never actually paints.
  return (
    <div ref={crumbBarRef} className={`flex-1 flex items-center min-w-0 ${compact ? 'gap-0.5 text-xs' : 'gap-2'}`}>
      <button
        onClick={() => onNavigate(null)}
        onDragOver={rootDropId && onCrumbDragOver ? (e) => onCrumbDragOver(e, rootDropId) : undefined}
        onDragLeave={onCrumbDragLeave}
        onDrop={rootDropId && onCrumbDrop ? (e) => onCrumbDrop(e, rootDropId) : undefined}
        data-testid="drive-breadcrumb-root"
        className={`${compact ? 'themed-text-muted hover:themed-text px-1 py-0.5 max-w-[10rem]' : 'themed-text font-medium hover:themed-icon-active px-2 py-1 max-w-[16rem]'} rounded transition-colors flex items-center shrink-0 ${
          rootDropId && dropTargetCrumb === rootDropId ? 'bg-green-600/30 ring-2 ring-green-500' : ''
        }`}
      >
        {squeezeRootCrumb ? (
          <Tooltip content={rootLabel} always className="flex items-center">
            <HardDrive size={compact ? 13 : 16} />
          </Tooltip>
        ) : (
          <Tooltip content={rootLabel} className="truncate">{rootLabel}</Tooltip>
        )}
      </button>
      {hiddenCrumbCount > 0 && (
        <span className={`flex items-center shrink-0 ${compact ? 'gap-0.5' : 'gap-2'}`}>
          <ChevronRight size={compact ? 12 : 14} className="themed-text-muted shrink-0" />
          <BreadcrumbOverflowMenu
            crumbs={breadcrumbs.slice(0, Math.min(hiddenCrumbCount, maxHiddenCrumbs))}
            dropTargetCrumb={dropTargetCrumb}
            onNavigate={onNavigate}
            onCrumbDragOver={onCrumbDragOver}
            onCrumbDragLeave={onCrumbDragLeave}
            onCrumbDrop={onCrumbDrop}
            t={t}
          />
        </span>
      )}
      {breadcrumbs.slice(Math.min(hiddenCrumbCount, maxHiddenCrumbs)).map((crumb, i, visible) => {
        const isCurrent = i === visible.length - 1
        return (
          <span key={crumb.id} className={`flex items-center ${compact ? 'gap-0.5' : 'gap-2'} ${isCurrent && squeezeCurrentCrumb ? 'min-w-0' : 'shrink-0'}`}>
            <ChevronRight size={compact ? 12 : 14} className="themed-text-muted shrink-0" />
            <button
              onClick={() => onNavigate(crumb.id)}
              onDragOver={onCrumbDragOver ? (e) => onCrumbDragOver(e, crumb.id) : undefined}
              onDragLeave={onCrumbDragLeave}
              onDrop={onCrumbDrop ? (e) => onCrumbDrop(e, crumb.id) : undefined}
              className={`themed-text-secondary hover:themed-text ${compact ? 'px-1 py-0.5' : 'px-2 py-1'} rounded transition-colors flex items-center gap-1.5 ${
                isCurrent ? (squeezeCurrentCrumb ? 'min-w-0' : '') : 'min-w-0 max-w-[8rem]'
              } ${dropTargetCrumb === crumb.id ? 'bg-green-600/30 ring-2 ring-green-500' : ''}`}
            >
              <CrumbLabel id={crumb.id} name={crumb.name} />
            </button>
          </span>
        )
      })}
    </div>
  )
}
